import proj4 from 'proj4';
import {
  MutableRefObject,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Camera, Vector3, WebGLRenderer } from 'three';
import { SRGBColorSpace } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

import { Levels } from '../../../../../typings/api/skymap/rest/v0/.common';
import { OsmOrthoTileStore } from '../../../stores/geodata-store-factory/ortho-tile-store/osm-ortho-tile-store';
import { isDefined } from '../../../utils/variables';
import { FlatPlaneWorldMapping } from '../../../viewer/geodata/ortho-tile-asset/flat-plane-world-mapping';
import { makeTileBoundsToWorldQuad } from '../../../viewer/geodata/ortho-tile-asset/make-tile-bounds-to-world-quad';
import { OrthoTileAsset } from '../../../viewer/geodata/ortho-tile-asset/ortho-tile-asset';
import { TileCache } from '../../../viewer/geodata/ortho-tile-asset/tile-cache';
import { TileLoader } from '../../../viewer/geodata/ortho-tile-asset/tile-loader';
import { renderOrders } from '../../../viewer/render-orders';
import { disposeRecursive } from '../../../viewer/utilities/three/dispose';
import { GeodataSpatialReferenceContext } from '../../components/cloud-processing/gcp-page/geodata-spatial-reference-state';

const WEB_MERCATOR_EXTENT = 20037508.342789244;
const WORLD_WIDTH_3857 = 2 * WEB_MERCATOR_EXTENT;

// Your existing converter is EPSG:3857 -> project.
// Therefore, converter.inverse([projectX, projectY]) gives [mercatorX, mercatorY].
function projectTo3857(
  webMercatorToProject: proj4.Converter,
  x: number,
  y: number,
): [number, number] {
  const [mx, my] = webMercatorToProject.inverse?.([x, y]) ?? [x, y];
  return [mx, my];
}

/**
 * Coarsest zoom where a tile is not larger than the bbox (strict in both dimensions).
 * Optional k lets you be stricter (k=2 => tile <= bbox/2).
 */
function minZoomForExtent3857(extentX: number, extentY: number, maxZoom = 19, k = 1): number {
  const extent = Math.min(extentX, extentY);
  if (extent <= 0) {
    return 0;
  }

  const tilesNeeded = (WORLD_WIDTH_3857 * k) / extent;
  const z = Math.ceil(Math.log2(tilesNeeded));
  return Math.max(0, Math.min(z, maxZoom));
}

/**
 * Create OSM-compatible tile levels for a square bbox around the project origin
 * (origin is in project CRS), using a radius in meters in the *project CRS*.
 *
 * IMPORTANT: radiusMeters assumes your project CRS units are meters.
 * If project CRS is degrees, use the "geodesic" variant instead (ask and I’ll paste it).
 */
function createOsmLevelsAroundOrigin(
  webMercatorToProject: proj4.Converter,
  originProject: { x: number; y: number },
  radiusMeters = 20_000,
  maxZoom = 19,
  minZoomTightness = 1, // 1 = tile <= bbox, 2 = tile <= bbox/2 (fewer coarse zooms)
): Levels[] {
  // Project CRS bbox around origin
  const x0p = originProject.x - radiusMeters;
  const x1p = originProject.x + radiusMeters;
  const y0p = originProject.y - radiusMeters;
  const y1p = originProject.y + radiusMeters;

  // Convert bbox corners to 3857
  const [mx0, my0] = projectTo3857(webMercatorToProject, x0p, y0p);
  const [mx1, my1] = projectTo3857(webMercatorToProject, x1p, y1p);

  const x0 = Math.min(mx0, mx1);
  const x1 = Math.max(mx0, mx1);
  const y0 = Math.min(my0, my1);
  const y1 = Math.max(my0, my1);

  // Clamp to valid mercator world (prevents weirdness near poles / bad transforms)
  const cx0 = Math.max(-WEB_MERCATOR_EXTENT, Math.min(WEB_MERCATOR_EXTENT, x0));
  const cx1 = Math.max(-WEB_MERCATOR_EXTENT, Math.min(WEB_MERCATOR_EXTENT, x1));
  const cy0 = Math.max(-WEB_MERCATOR_EXTENT, Math.min(WEB_MERCATOR_EXTENT, y0));
  const cy1 = Math.max(-WEB_MERCATOR_EXTENT, Math.min(WEB_MERCATOR_EXTENT, y1));

  const extentX = cx1 - cx0;
  const extentY = cy1 - cy0;

  const minZoom = minZoomForExtent3857(extentX, extentY, maxZoom, minZoomTightness);

  const worldMin = -WEB_MERCATOR_EXTENT;
  const worldMax = WEB_MERCATOR_EXTENT;

  const levels: Levels[] = [];

  for (let z = minZoom; z <= maxZoom; z++) {
    const n = 1 << z;
    const tileSize = (worldMax - worldMin) / n;

    // Intersecting tiles (recommended)
    const colMin = Math.floor((cx0 - worldMin) / tileSize);
    const colMax = Math.ceil((cx1 - worldMin) / tileSize) - 1;

    const rowMin = Math.floor((worldMax - cy1) / tileSize);
    const rowMax = Math.ceil((worldMax - cy0) / tileSize) - 1;

    const columnMin = Math.max(0, Math.min(colMin, n - 1));
    const columnMax = Math.max(0, Math.min(colMax, n - 1));
    const rowMinClamped = Math.max(0, Math.min(rowMin, n - 1));
    const rowMaxClamped = Math.max(0, Math.min(rowMax, n - 1));

    if (columnMin <= columnMax && rowMinClamped <= rowMaxClamped) {
      levels.push({ zoom: z, columnMin, columnMax, rowMin: rowMinClamped, rowMax: rowMaxClamped });
    }
  }

  return levels;
}

export function useWms(
  _renderTarget: RefObject<HTMLDivElement>,
  _controls: OrbitControls | undefined,
  showWms: boolean,
  wmsUpdateRef?: MutableRefObject<((camera: Camera, renderer: WebGLRenderer) => void) | undefined>,
  customProjection?: string,
  customOrigo?: Vector3 | null,
) {
  const context = useContext(GeodataSpatialReferenceContext);

  const projection = useMemo(() => {
    return context ? context.projection : customProjection;
  }, [context, customProjection]);

  const origo = useMemo(() => {
    return context ? context.origo : customOrigo;
  }, [context, customOrigo]);

  const [orthoAsset, setOrthoAsset] = useState<OrthoTileAsset | null>(null);
  const orthoAssetRef = useRef<OrthoTileAsset | null>(null);

  const useOrthoAsset = showWms && isDefined(projection) && projection.trim() !== '';

  useEffect(() => {
    if (!useOrthoAsset || !origo) {
      if (orthoAssetRef.current) {
        disposeRecursive(orthoAssetRef.current);
        orthoAssetRef.current = null;
        setOrthoAsset(null);
      }
      return;
    }

    const webMercatorToProject = proj4('EPSG:3857', projection);
    const sceneOriginProject = { x: origo.x, y: origo.y };

    const store = new OsmOrthoTileStore();
    const loader = new TileLoader({ store });
    const cache = new TileCache({ maxTextures: 512 });

    const levels = createOsmLevelsAroundOrigin(
      webMercatorToProject,
      sceneOriginProject,
      100_000,
      19,
      2,
    );

    const asset = new OrthoTileAsset({
      id: 'wms-overlay',
      loader,
      cache,
      levels,
      worldMapping: new FlatPlaneWorldMapping({
        tileBoundsToWorldQuad: makeTileBoundsToWorldQuad({
          webMercatorToProject,
          sceneOriginProject,
        }),
        colorSpace: SRGBColorSpace,
        anisotropy: 8,
        depthWrite: false,
        renderOrder: renderOrders.wmsMap,
        transparent: false,
      }),
    });
    orthoAssetRef.current = asset;
    setOrthoAsset(asset);

    return () => {
      disposeRecursive(asset);
      orthoAssetRef.current = null;
      setOrthoAsset(null);
    };
  }, [useOrthoAsset, projection, origo]);

  const wmsQuad = useOrthoAsset ? orthoAsset ?? undefined : undefined;

  const updateWms = useCallback((camera: Camera, renderer: WebGLRenderer) => {
    orthoAssetRef.current?.update(camera, renderer);
  }, []);

  if (wmsUpdateRef) {
    wmsUpdateRef.current = useOrthoAsset ? updateWms : undefined;
  }

  return { wmsQuad, updateWms };
}
